import { ActionTree } from 'vuex'
import { getRoom } from './helper'
import { IChatNewState, UnreadMap } from './types'
import groupBy from 'lodash/groupBy'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import uniqueId from 'lodash/uniqueId'
import { MessageTypes } from '@/constants/chat/MessageTypes'
import { RoomStages } from '@/constants/chat/RoomStages'
import { RoomTypes } from '@/constants/chat/RoomTypes'
import { Roles } from '@/constants/user/roles'
import ChatParticipant from '@/models-ts/chat/ChatParticipant'
import ChatRoom from '@/models-ts/chat/ChatRoom'
import ChatRoomDetails from '@/models-ts/chat/ChatRoomDetails'
import { ChatMessage } from '@/models-ts/chat/messages/ChatMessageType'
import FileChatMessage from '@/models-ts/chat/messages/FileChatMessage'
import ImageChatMessage from '@/models-ts/chat/messages/ImageChatMessage'
import TextChatMessage from '@/models-ts/chat/messages/TextChatMessage'
import chatService, { RoomFilter } from '@/servicies-ts/Chat/Chat'
import { ChatEvents } from '@/servicies-ts/Chat/types'
import { generateChatRef, generateId } from '@/utils-ts/chats'
import { convertToUTC } from '@/utils/date'
import { isImage, readImage } from '@/utils/file'

const LIMIT_ROOMS = 10
const field = (isCustomer: boolean) => (isCustomer ? 'customerStages' : 'freelancerStages')
const notArchivedStages = [RoomStages.NEW, RoomStages.IN_PROGRESS, RoomStages.IN_DISPUTE, RoomStages.WAIT_FOR_REVIEW]
const FREELANCE_ROOMS_FILTER = (isCustomer: boolean): RoomFilter => ({
  [field(isCustomer)]: notArchivedStages,
  types: [RoomTypes.GIG, RoomTypes.JOB],
})
const VACANCIES_ROOMS_FILTER = (isCustomer: boolean): RoomFilter => ({
  [field(isCustomer)]: notArchivedStages,
  types: [RoomTypes.VACANCY],
})
const ARCHIVED_ROOMS_FILTER = (isCustomer: boolean): RoomFilter => ({ [field(isCustomer)]: [RoomStages.ARCHIVED] })
const arrayToMap = (rooms: Array<ChatRoom>) => rooms.reduce((map, room) => ({ ...map, [room.id]: room }), {})

export default {
  async init({ rootState, commit, dispatch }) {
    commit('setClientId', generateId())
    await chatService.init(
      { token: rootState.user.accessToken as string },
      {
        [ChatEvents.CONNECTED]: () => {
          console.log('connected')
          setTimeout(() => {
            // doesn't work without delay
            commit('setConnected', true)
            commit('setInitialized', true)
          }, 2000)
        },
        [ChatEvents.DISCONNECT]: (reason) => {
          console.log('disconntected', reason, new Date())
          commit('setConnected', false)
        },
        [ChatEvents.RECEIVE_ROOMS]: (response) => dispatch('onReceiveRooms', response),
        [ChatEvents.RECEIVE_ROOM]: (room) => dispatch('onReceiveRoom', room),
        [ChatEvents.RECEIVE_ROOM_DETAILS]: (room) => dispatch('onReceiveRoomDetails', room),
        [ChatEvents.RECEIVE_MESSAGES]: (messages) => dispatch('onReceiveMessages', messages),
        [ChatEvents.RECEIVE_MESSAGE]: (message, rawData) => dispatch('onReceiveMessage', { message, rawData }),
        [ChatEvents.RECEIVE_FILE]: (message, rawData) => dispatch('onReceiveMessage', { message, rawData }),
        [ChatEvents.UPDATE_MESSAGE]: (message) => dispatch('onUpdateMessage', message),
        [ChatEvents.ROOM_ACTION]: (room) => dispatch('onUpdateRoom', room),
        [ChatEvents.UNLOCK_ROOM]: (room) => dispatch('onUnlockRoom', room),
        [ChatEvents.RECEIVE_UNREAD_MESSAGES_COUNT]: (msg) => dispatch('onReceiveUnreadMessagesCount', msg),
      }
    )
  },
  destroy({ commit }) {
    commit('setInitialized', false)
    commit('setConnected', false)
    chatService.destroy()
  },
  initRooms({ state, commit, rootGetters, dispatch }) {
    const activeRole = rootGetters['user/activeProfile']
    if (activeRole) {
      const isCustomer = activeRole === Roles.CUSTOMER

      const freelanceRoomsParams = { ...FREELANCE_ROOMS_FILTER(isCustomer), limit: LIMIT_ROOMS, offset: 0 }
      const vacanciesRoomsParams = { ...VACANCIES_ROOMS_FILTER(isCustomer), limit: LIMIT_ROOMS, offset: 0 }
      const archivedRoomsFilters = { ...ARCHIVED_ROOMS_FILTER(isCustomer), limit: LIMIT_ROOMS, offset: 0 }

      dispatch('getRooms', state.lastFiltersReq.freelance || freelanceRoomsParams)
      dispatch('getRooms', state.lastFiltersReq.vacancies || vacanciesRoomsParams)
      dispatch('getRooms', state.lastFiltersReq.archived || archivedRoomsFilters)
    }
  },
  async loadMoreFreelanceRooms({ rootGetters, commit, state, dispatch }, { limit = LIMIT_ROOMS, offset = 0 }) {
    // If it isn't pagination request lastSearch !== search, we will reset our chats in store
    if (state.lastSearch !== state.search) {
      commit('setFreelanceRooms', {
        pagination: { limit, offset },
        values: {},
      })
    }
    commit('setFreelanceRoomsLoading')
    const activeRole = rootGetters['user/activeProfile']
    if (activeRole) {
      const isCustomer = activeRole === Roles.CUSTOMER
      const roomsParams = { ...FREELANCE_ROOMS_FILTER(isCustomer), limit, offset, entityName: state.search }
      dispatch('getRooms', roomsParams)
    }
  },
  async loadMoreVacanciesRooms({ rootGetters, commit, state, dispatch }, { limit = LIMIT_ROOMS, offset = 0 }) {
    // If it isn't pagination request lastSearch !== search, we will reset our chats in store
    if (state.lastSearch !== state.search) {
      commit('setVacanciesRooms', {
        pagination: { limit, offset },
        values: {},
      })
    }
    commit('setVacanciesRoomsLoading')
    const activeRole = rootGetters['user/activeProfile']
    if (activeRole) {
      const isCustomer = activeRole === Roles.CUSTOMER
      const roomsParams = { ...VACANCIES_ROOMS_FILTER(isCustomer), limit, offset, entityName: state.search }
      dispatch('getRooms', roomsParams)
    }
  },
  async loadMoreArchivedRooms({ rootGetters, commit, state, dispatch }, { limit = LIMIT_ROOMS, offset = 0 }) {
    // If it isn't pagination request lastSearch !== search, we will reset our chats in store
    if (state.lastSearch !== state.search) {
      commit('setArchivedRooms', {
        pagination: { limit, offset },
        values: {},
      })
    }
    commit('setArchivedRoomsLoading')
    const activeRole = rootGetters['user/activeProfile']
    if (activeRole) {
      const isCustomer = activeRole === Roles.CUSTOMER
      const roomsParams = { ...ARCHIVED_ROOMS_FILTER(isCustomer), limit, offset, entityName: state.search }
      dispatch('getRooms', roomsParams)
    }
  },
  getRooms({ state, commit, rootGetters }, filters: RoomFilter) {
    let filtersWithRef = filters
    const isCustomer = rootGetters['user/activeProfile'] === Roles.CUSTOMER
    const filterStage = field(isCustomer)
    const comparableFilter = pick(filters, [filterStage, 'types'])

    if (isEqual(comparableFilter, FREELANCE_ROOMS_FILTER(isCustomer))) {
      filtersWithRef = { ...filters, ref: generateChatRef(state.clientId, 'freelance') }
      commit('setLastFreelanceFiltersReq', filtersWithRef)
    } else if (isEqual(comparableFilter, VACANCIES_ROOMS_FILTER(isCustomer))) {
      filtersWithRef = { ...filters, ref: generateChatRef(state.clientId, 'vacancies') }
      commit('setLastVacanciesFiltersReq', filtersWithRef)
    } else if (isEqual(comparableFilter, ARCHIVED_ROOMS_FILTER(isCustomer))) {
      filtersWithRef = { ...filters, ref: generateChatRef(state.clientId, 'archived') }
      commit('setLastArchivedFiltersReq', filtersWithRef)
    }

    chatService.getRooms(filtersWithRef)
  },
  getUnreadMessagesCount() {
    chatService.getUnreadMessagesCount()
  },
  onReceiveUnreadMessagesCount({ commit }, unreadMap: UnreadMap) {
    commit('setUnreadMap', unreadMap)
  },
  onReceiveRooms({ commit, rootGetters, state }, { filter, rooms, total }: { filter: RoomFilter; rooms: Array<ChatRoom>; total: number }) {
    const isCustomer = rootGetters['user/activeProfile'] === Roles.CUSTOMER
    const participants = rooms.reduce(
      (res, room) => {
        room.participants.forEach((user) => {
          res[user.id] = user
        })
        return res
      },
      {} as IChatNewState['participants']
    )
    Object.values(participants).forEach((user) => commit('addParticipant', user))

    const filterStage = field(isCustomer)
    const comparableFilter = pick(filter, [filterStage, 'types'])
    if (isEqual(comparableFilter, FREELANCE_ROOMS_FILTER(isCustomer))) {
      if (state.lastFiltersReq.freelance?.ref === filter.ref) {
        // If lastSearch == entityName it will be pagination
        commit(state.lastSearch === filter.entityName ? 'setFreelanceRoomsMore' : 'setFreelanceRooms', {
          pagination: { total, limit: filter.limit, offset: filter.offset },
          values: arrayToMap(rooms),
        })
      }
    } else if (isEqual(comparableFilter, VACANCIES_ROOMS_FILTER(isCustomer))) {
      if (state.lastFiltersReq.vacancies?.ref === filter.ref) {
        // If lastSearch == entityName it will be pagination
        commit(state.lastSearch === filter.entityName ? 'setVacanciesRoomsMore' : 'setVacanciesRooms', {
          pagination: { total, limit: filter.limit, offset: filter.offset },
          values: arrayToMap(rooms),
        })
      }
    } else if (isEqual(comparableFilter, ARCHIVED_ROOMS_FILTER(isCustomer))) {
      if (state.lastFiltersReq.archived?.ref === filter.ref) {
        // If lastSearch == entityName it will be pagination
        commit(state.lastSearch === filter.entityName ? 'setArchivedRoomsMore' : 'setArchivedRooms', {
          pagination: { total, limit: filter.limit, offset: filter.offset },
          values: arrayToMap(rooms),
        })
      }
    }
  },
  onUpdateRoom({ commit, rootGetters }, room: ChatRoom) {
    const isCustomer = rootGetters['user/activeProfile'] === Roles.CUSTOMER
    const stage = isCustomer ? room.customerStage : room.freelancerStage
    const isArchived = stage === RoomStages.ARCHIVED
    const isVacancy = room.type === RoomTypes.VACANCY

    if (isArchived) {
      commit('setRoom', { roomId: room.id, room, roomStorage: 'archivedRooms' })
      if (isVacancy) {
        commit('setRoom', { roomId: room.id, room: null, roomStorage: 'vacanciesRooms' })
      } else {
        commit('setRoom', { roomId: room.id, room: null, roomStorage: 'freelanceRooms' })
      }
    } else {
      if (isVacancy) {
        commit('setRoom', { roomId: room.id, room, roomStorage: 'vacanciesRooms' })
      } else {
        commit('setRoom', { roomId: room.id, room, roomStorage: 'freelanceRooms' })
      }
    }
  },
  onUnlockRoom({ commit }, { roomId, flag }: { roomId: string; flag: boolean }) {
    commit('setRoomUnlocked', { roomId, flag })
  },
  onReceiveRoom({ state, commit, dispatch, rootGetters }, room: ChatRoom) {
    room.participants.forEach((user) => commit('addParticipant', user))

    if (state.openedRoomId) {
      const openedRoom = state.vacanciesRooms.values[state.openedRoomId]
      if (openedRoom && openedRoom.temporary) {
        // commit('replaceRoom', { id: state.openedRoomId, room })
        commit('setOpenedRoomId', room.id)
        dispatch('openRoom', room.id)
        return
      }
    }
    const isCustomer = rootGetters['user/activeProfile'] === Roles.CUSTOMER
    const stage = isCustomer ? room.customerStage : room.freelancerStage
    const isArchived = stage === RoomStages.ARCHIVED
    const isVacancy = room.type === RoomTypes.VACANCY
    if (isArchived) {
      commit('setRoom', { roomId: room.id, room, roomStorage: 'archivedRooms' })
    } else if (isVacancy) {
      commit('setRoom', { roomId: room.id, room, roomStorage: 'vacanciesRooms' })
    } else {
      commit('setRoom', { roomId: room.id, room, roomStorage: 'freelanceRooms' })
    }
  },
  onReceiveRoomDetails({ commit }, room: ChatRoomDetails) {
    room.participants.forEach((user) => commit('addParticipant', user))
    commit('setOpenedRoom', { roomId: room.id, room })
  },
  onReceiveMessages({ state, commit }, messages: Array<ChatMessage>) {
    const messagesByRoom = groupBy(messages, 'roomId')
    if (state.openedRoomId) {
      if (messagesByRoom[state.openedRoomId]?.length) {
        for (const message of messagesByRoom[state.openedRoomId]) {
          commit('setRoomMessage', { roomId: state.openedRoomId, message })
        }
      } else {
        commit('setRoomMessage', { roomId: state.openedRoomId })
      }
    }
  },
  onReceiveMessage(
    { rootState, state, getters, commit, dispatch },
    { message, rawData }: { message: ChatMessage; rawData: { room: LX.ChatRoom } }
  ) {
    if (state.openedRoomId && message.roomId === state.openedRoomId) {
      commit('setRoomMessage', { roomId: state.openedRoomId, message })
    }
    const room = getRoom(state, message.roomId)

    if (room) {
      commit('setLastMessage', { roomId: room.id, message })
      let chatUserId = `${rootState.user.id}:${rootState.user.activeRole}`
      if (!message.readers.find((reader) => reader.readerId === chatUserId)) {
        commit('setRoomUnreadCount', { roomId: room.id, count: getters['getUnreadCount'](room.id) + 1 })
      }
    } else {
      const newRoom = ChatRoom.fromServer(rawData.room, message)
      const unreadCount = getters['getUnreadCount'](newRoom.id) || 1
      newRoom.unreadCount = unreadCount
      dispatch('onReceiveRoom', newRoom)
    }
  },
  onUpdateMessage({ state, commit }, message: ChatMessage) {
    if (state.openedRoomId && message.roomId === state.openedRoomId) {
      commit('setRoomMessage', { roomId: state.openedRoomId, message })
    }
  },
  sendMessage({ commit, rootState }, { message, roomId }: { message: string; roomId: string }) {
    const userId = ChatParticipant.toSenderId(rootState.user.id, rootState.user.activeRole)
    const id = uniqueId('msg_')
    const messageModel = new TextChatMessage({
      _id: id,
      deliveryId: id,
      type: MessageTypes.TEXT,
      updatedAt: convertToUTC(new Date()),
      roomId,
      body: message,
      sender: { id: userId },
      sending: true,
      readers: [],
      isSystem: false,
    })
    commit('addMessage', { roomId, message: messageModel })
    chatService.sendMessage({ message, roomId, id })
  },
  async sendFile({ commit, rootState }, { file, roomId }: { file: File; roomId: string }) {
    const userId = ChatParticipant.toSenderId(rootState.user.id, rootState.user.activeRole)
    const id = uniqueId('msg_')
    let messageModel
    chatService.sendFile({ file, id, roomId })
    if (isImage(file)) {
      const { base64 } = await readImage(file, null)
      messageModel = new ImageChatMessage({
        _id: id,
        deliveryId: id,
        type: MessageTypes.IMAGE,
        updatedAt: convertToUTC(new Date()),
        roomId,
        body: {
          name: file.name,
          url: null,
          size: file.size,
        },
        sender: { id: userId },
        sending: true,
        base64,
        readers: [],
        isSystem: false,
      })
    } else {
      messageModel = new FileChatMessage({
        _id: id,
        deliveryId: id,
        type: MessageTypes.FILE,
        updatedAt: convertToUTC(new Date()),
        roomId,
        body: {
          name: file.name,
          description: file.name,
          url: null,
          size: file.size,
        },
        sender: { id: userId },
        sending: true,
        readers: [],
        isSystem: false,
      })
    }
    commit('addMessage', { roomId, message: messageModel })
  },
  createRoom({ commit }, room: ChatRoom) {
    commit('setRoom', { roomId: room.id, room, roomStorage: 'vacanciesRooms' })
    commit('setOpenedRoom', { roomId: room.id, room })
    return room
  },
  openRoom({ commit }, roomId: string) {
    commit('setOpenedRoomId', roomId)
    if (!roomId.startsWith('tmp_')) {
      commit('setRoomMessagesLoading', roomId)
      chatService.getRoom(roomId)
      chatService.getRoomMessages(roomId)
    }
  },
  readMessages({ commit }, { roomId, ids, ownId }: { roomId: string; ids: Array<string>; ownId: string }) {
    chatService.readMessages({ roomId, ids })
    commit('readMessages', { roomId, ids, ownId })
  },
  async closeRoom({ commit }, roomId: string) {
    commit('setOpenedRoomId', null)
    // await chatService.closeRoom(roomId)
  },
} as ActionTree<IChatNewState, any>
